<template>
  <div 
    :class="[
      'tier-badge',
      `tier-badge--${variant}`,
      `tier-badge--${tierClass}`,
      { 'tier-badge--interactive': clickable }
    ]"
    :tabindex="clickable ? 0 : -1"
    :role="clickable ? 'button' : null"
    :aria-label="accessibilityLabel"
    @click="handleClick"
    @keydown.enter="handleClick"
    @keydown.space.prevent="handleClick"
  >
    <!-- Icon/Medal Display - Removed for cleaner typography -->
    <!-- Icons removed per user feedback -->
    
    <!-- Tier Text -->
    <span 
      :class="['tier-badge__text', `tier-badge__text--${variant}`]"
    >
      <Skull v-if="tier === 'Tiering Herrie'" :size="variant === 'large' ? 18 : variant === 'medal' ? 16 : 12" class="skull-icon" />
      {{ displayText }}
    </span>
    
    <!-- Optional Badge Glow Effect -->
    <div 
      v-if="variant === 'medal' || glowEffect" 
      :class="['tier-badge__glow', `tier-badge__glow--${tierClass}`]"
    ></div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { Skull } from 'lucide-vue-next'

export default {
  name: 'EnhancedTierBadge',
  components: {
    Skull
  },
  props: {
    tier: {
      type: String,
      required: true,
      validator: (value) => [
        'S', 'A', 'B', 'C', 'D', 'E', 'F',
        'Not Rated Yet', 'Not rated yet', 
        'Now Listening To', 'now-listening-to',
        'Tiering Herrie', 'tiering-herrie',
        '?', 'question'
      ].includes(value)
    },
    variant: {
      type: String,
      default: 'badge',
      validator: (value) => ['badge', 'medal', 'compact'].includes(value)
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    clickable: {
      type: Boolean,
      default: false
    },
    glowEffect: {
      type: Boolean,
      default: false
    },
    customText: {
      type: String,
      default: null
    }
  },
  emits: ['click'],
  setup(props, { emit }) {
    // Tier class mapping for CSS
    const tierClass = computed(() => {
      return props.tier.toLowerCase().replace(/\s+/g, '-')
    })

    // Tier icon mapping
    const tierIconMap = {
      'S': '🏆',
      'A': '🥇',
      'B': '🥈',
      'C': '🥉',
      'D': '📀',
      'E': '💿',
      'Not Rated Yet': '❓',
      'Now Listening To': '🎵'
    }

    const tierIcon = computed(() => {
      return tierIconMap[props.tier] || '❓'
    })

    // Display text logic
    const displayText = computed(() => {
      if (props.customText) {
        return props.customText
      }
      
      if (props.variant === 'compact') {
        return props.tier === 'Not Rated Yet' ? '?' : 
               props.tier === 'Now Listening To' ? '♪' : 
               props.tier
      }
      
      return props.tier
    })

    // Accessibility label
    const accessibilityLabel = computed(() => {
      const tierName = props.tier === 'Not Rated Yet' ? 'Not yet rated' :
                      props.tier === 'Now Listening To' ? 'Currently listening to' :
                      `${props.tier} tier`
      
      return props.clickable ? 
        `${tierName} badge, clickable` : 
        `${tierName} badge`
    })

    // Click handler
    const handleClick = () => {
      if (props.clickable) {
        emit('click', props.tier)
      }
    }

    return {
      tierClass,
      tierIcon,
      displayText,
      accessibilityLabel,
      handleClick
    }
  }
}
</script>

<style scoped>
.tier-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 12px;
  font-weight: 600;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  user-select: none;
}

.tier-badge::before {
  display: none;
}

.tier-badge::after {
  display: none;
}

/* Variant Styles */
.tier-badge--badge {
  padding: 8px 16px;
  font-size: 14px;
  min-width: 60px;
  height: 36px;
  gap: 0;
}

.tier-badge--medal {
  padding: 12px 20px;
  font-size: 18px;
  min-width: 80px;
  height: 48px;
  gap: 0;
  border-radius: 14px;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 800;
  border-width: 1.5px;
}

.tier-badge--compact {
  padding: 5px 10px;
  font-size: 12px;
  min-width: 36px;
  height: 24px;
  gap: 0;
  border-radius: 8px;
}

/* Size Modifiers */
.tier-badge--small {
  transform: scale(0.85);
}

.tier-badge--large {
  transform: scale(1.15);
}

/* Interactive States */
.tier-badge--interactive {
  cursor: pointer;
}

.tier-badge--interactive:hover {
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}


.tier-badge--interactive:focus-visible {
  outline: 3px solid rgba(255, 255, 255, 0.8);
  outline-offset: 2px;
}

.tier-badge--interactive:active {
  opacity: 0.9;
}

/* Icon Styles */
.tier-badge__icon {
  font-size: 1.2em;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tier-badge__icon--medal {
  font-size: 1.4em;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.tier-badge__icon--compact {
  font-size: 1em;
}

/* Text Styles */
.tier-badge__text {
  font-weight: 700;
  letter-spacing: 1px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  text-transform: uppercase;
  font-family: 'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif;
}

.tier-badge__text--medal {
  font-size: 1em;
  font-weight: 800;
  letter-spacing: 1.2px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.4);
}

.tier-badge__text--badge {
  font-weight: 700;
  letter-spacing: 0.8px;
}

.tier-badge__text--compact {
  font-size: 0.9em;
  font-weight: 600;
  letter-spacing: 0.6px;
}

/* Glow Effect */
.tier-badge__glow {
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  border-radius: inherit;
  opacity: 0;
  transition: var(--tier-transition-smooth);
  pointer-events: none;
  z-index: -1;
}

.tier-badge--medal .tier-badge__glow,
.tier-badge--interactive:hover .tier-badge__glow {
  opacity: 0.6;
}

/* Tier-Specific Styling with Glass Effect */
.tier-badge--s {
  background: rgba(240, 0, 0, 0.8);
  color: white;
  border-color: var(--tier-s-border);
  box-shadow: 0 4px 12px rgba(240, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
}

.tier-badge--s::after {
  display: none;
}


.tier-badge--a {
  background: rgba(255, 125, 0, 0.8);
  color: white;
  border-color: var(--tier-a-border);
  box-shadow: 0 4px 12px rgba(255, 125, 0, 0.2);
}

.tier-badge--b {
  background: rgba(255, 233, 2, 0.8);
  color: white;
  border-color: var(--tier-b-border);
  box-shadow: 0 4px 12px rgba(255, 233, 2, 0.2);
}

.tier-badge--c {
  background: rgba(123, 192, 67, 0.8);
  color: white;
  border-color: rgba(123, 192, 67, 0.4);
  box-shadow: 0 4px 12px rgba(123, 192, 67, 0.2);
}

.tier-badge--d {
  background: rgba(31, 179, 211, 0.8);
  color: white;
  border-color: rgba(31, 179, 211, 0.4);
  box-shadow: 0 4px 12px rgba(31, 179, 211, 0.2);
}

.tier-badge--e {
  background: rgba(93, 115, 126, 0.8);
  color: white;
  border-color: rgba(93, 115, 126, 0.4);
  box-shadow: 0 4px 12px rgba(93, 115, 126, 0.2);
}

.tier-badge--not-rated-yet {
  background: rgba(156, 163, 175, 0.8);
  color: white;
  border-color: rgba(156, 163, 175, 0.4);
  box-shadow: 0 4px 12px rgba(156, 163, 175, 0.2);
}

.tier-badge--now-listening-to {
  background: rgba(139, 92, 246, 0.8);
  color: white;
  border-color: rgba(139, 92, 246, 0.4);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.2);
}

.tier-badge--tiering-herrie {
  background: rgba(90, 109, 122, 0.9);
  color: white;
  border-color: rgba(90, 109, 122, 0.6);
  box-shadow: 0 4px 12px rgba(122, 138, 153, 0.2);
  position: relative;
  overflow: hidden;
}

.tier-badge--tiering-herrie::before {
  display: none;
}

.tier-badge--tiering-herrie::after {
  display: none;
}



/* Glow Colors */
.tier-badge__glow--s { 
  background: radial-gradient(circle, 
    rgba(240, 0, 0, 0.4) 0%, 
    transparent 70%); 
}

.tier-badge__glow--a { background: radial-gradient(circle, var(--tier-a-glow) 0%, transparent 70%); }
.tier-badge__glow--b { background: radial-gradient(circle, var(--tier-b-glow) 0%, transparent 70%); }
.tier-badge__glow--c { background: radial-gradient(circle, var(--tier-c-glow) 0%, transparent 70%); }
.tier-badge__glow--d { background: radial-gradient(circle, var(--tier-d-glow) 0%, transparent 70%); }
.tier-badge__glow--e { background: radial-gradient(circle, var(--tier-e-glow) 0%, transparent 70%); }
.tier-badge__glow--not-rated-yet { background: radial-gradient(circle, var(--tier-not-rated-glow) 0%, transparent 70%); }
.tier-badge__glow--now-listening-to { background: radial-gradient(circle, var(--tier-now-listening-glow) 0%, transparent 70%); }
.tier-badge__glow--tiering-herrie { background: radial-gradient(circle, rgba(122, 138, 153, 0.4) 0%, transparent 70%); }

/* Skull Icon Styling */
.skull-icon {
  margin-right: 6px;
  vertical-align: middle;
  opacity: 0.95;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.4));
  display: inline-flex;
  align-items: center;
}

/* Responsive Design */
@media (max-width: 768px) {
  .tier-badge--badge {
    padding: 6px 12px;
    font-size: 13px;
    min-width: 50px;
    height: 32px;
  }
  
  .tier-badge--medal {
    padding: 10px 16px;
    font-size: 15px;
    min-width: 70px;
    height: 42px;
  }
  
  .tier-badge--compact {
    padding: 3px 6px;
    font-size: 11px;
    min-width: 28px;
    height: 20px;
  }
}

@media (max-width: 480px) {
  .tier-badge--badge {
    padding: 4px 8px;
    font-size: 12px;
    min-width: 40px;
    height: 28px;
    gap: 4px;
  }
  
  .tier-badge--medal {
    padding: 8px 12px;
    font-size: 14px;
    min-width: 60px;
    height: 36px;
    gap: 6px;
  }
}
</style>